import React, { useCallback, useRef } from 'react';
import { Button } from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import { resolveButtonLink } from '../../../../shared/boxes/button/buttonLink';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { buttonAppearance, ctaButtonClasses, startRippleOn } from './buttonAppearance';

/**
 * Bouton d'appel à l'action, partagé par la box Bouton, la Bannière d'action et
 * les slides du Carrousel — trois rendus jusqu'ici identiques à quelques props
 * près.
 *
 * Le libellé s'écrit sur le canvas comme un titre (`contentProp` pour une prop
 * racine, `contentPath` pour un item de liste) ; seuls la cible et l'apparence
 * restent en sidebar.
 *
 * En édition le bouton n'est ni un lien ni cliquable : sinon un clic destiné à
 * placer le curseur déclencherait la navigation.
 *
 * L'apparence se résout à partir des props du bloc (`buttonAppearance`) : un
 * bouton sans objet `buttonStyle` ni `border` rend exactement ce qu'il rendait
 * avant le chantier.
 */
export default function CtaButton({
  id,
  puck,
  label,
  contentProp,
  contentPath,
  /** Ancienne cible, texte libre. Lue quand `link` est absent. */
  url,
  /** Cible typée : page, bloc, URL, e-mail, téléphone. */
  link,
  /** Classe du libellé lui-même (le Carrousel lui impose une largeur mini). */
  labelClassName,
  /** Style par défaut du libellé (police, taille, couleur…). */
  labelStyle,
  className = '',
  /** Repère en édition quand le libellé est vidé — le bouton paraîtrait cassé. */
  placeholder = 'Texte du bouton',
  /** Apparence : lues telles quelles par buttonAppearance. */
  color,
  size,
  radius,
  border,
  buttonStyle,
  /** Nom d'icône Tabler. Son côté est réglé dans `buttonStyle.iconSide`. */
  icon,
  iconColor,
  /** Sur-titre au-dessus du libellé (« Chapitre suivant »), écrit sur le
   *  canvas comme le libellé ; `null` = pas de sur-titre. */
  caption = null,
  captionProp,
  captionStyle,
  /** Toute la largeur de sa colonne, icône repoussée au bord opposé. */
  fullWidth = false,
  ...rest
}) {
  const isEditing = !!puck?.isEditing;
  const appearance = buttonAppearance(
    { color, size, radius, border, buttonStyle },
    `${isEditing ? ctaButtonClasses.editing : ''} ${className}`.trim(),
  );
  const anchorProps = isEditing ? {} : resolveButtonLink(link, url);
  const rippleRef = useRef(null);

  const startRipple = useCallback((event) => {
    startRippleOn(rippleRef.current, event);
  }, []);

  const Icon = icon ? TablerIcons[icon] : null;
  const iconNode = Icon
    ? <Icon size={18} stroke={1.5} color={iconColor || undefined} />
    : null;
  const iconOnly = appearance.iconSide === 'only' && !!iconNode;
  const isRipple = appearance.pressAnimation === 'ripple';

  return (
    <Button
      ref={isRipple ? rippleRef : undefined}
      onPointerDown={isRipple ? startRipple : undefined}
      component={isEditing ? 'div' : 'a'}
      {...anchorProps}
      {...appearance.buttonProps}
      // Une icône seule n'a plus de texte : sans libellé accessible, le bouton
      // est muet pour un lecteur d'écran.
      aria-label={iconOnly && !isEditing ? plainText(label) || placeholder : undefined}
      leftSection={appearance.iconSide === 'left' && !iconOnly ? iconNode : undefined}
      rightSection={appearance.iconSide === 'right' && !iconOnly ? iconNode : undefined}
      className={`${appearance.className} ${caption !== null ? ctaButtonClasses.withCaption : ''}`.trim()}
      fullWidth={fullWidth}
      justify={fullWidth && appearance.iconSide === 'right' ? 'space-between' : undefined}
      {...rest}
    >
      {/* En édition le libellé reste éditable même en mode « icône seule » :
          sinon il n'y aurait plus aucun moyen de le corriger. */}
      {iconOnly && !isEditing ? iconNode : (
        <CaptionedLabel
          caption={caption !== null && (
            <PuckInlineRichText
              id={id}
              puck={puck}
              html={caption}
              contentProp={captionProp}
              className={ctaButtonClasses.caption}
              style={{ color: 'inherit', fontFamily: 'inherit', ...captionStyle }}
              placeholder="Sur-titre"
              drawMode
              reducedToolbar
            />
          )}
        >
          <PuckInlineRichText
            id={id}
            puck={puck}
            html={label}
            contentProp={contentProp}
            contentPath={contentPath}
            className={labelClassName}
            style={{ color: 'inherit', fontSize: 'inherit', fontFamily: 'inherit', ...labelStyle }}
            placeholder={placeholder}
            drawMode
            reducedToolbar
          />
        </CaptionedLabel>
      )}
    </Button>
  );
}

/** Sans sur-titre, le libellé reste seul : rien ne change pour les boutons
 *  existants. */
function CaptionedLabel({ caption, children }) {
  if (!caption) return children;
  return (
    <span className={ctaButtonClasses.captioned}>
      {caption}
      {children}
    </span>
  );
}

/** Libellé riche dépouillé de ses balises, pour un attribut accessible. */
function plainText(html) {
  return String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}
